import { useMemo } from 'react'
import type { OrpcClient } from '@/orpc/client'
import { usePatientAiReport, type AiReportOps } from './usePatientAiReport'
import { useClient } from './useClient'

// Stored Risks & Gaps report + staleness state served by GET /v2/patient/{id}/risks-gaps.
// `upToDate` covers BOTH staleness inputs (chart details + guideline selection/citations)
// and is only present on responses that opted into the check.
export type RisksGapsData = Awaited<ReturnType<OrpcClient['patientRisksGaps']>>

// The hook's full surface — passed down from the RisksGaps container to the header status
// controls and the report panel, which share one instance.
export type UsePatientRisksGapsResult = ReturnType<typeof usePatientRisksGaps>

// The Risks & Gaps instantiation of the shared ai-report hook (usePatientAiReport carries
// the load/verify/auto-rebuild/poll behavior). Note the auto-rebuild: visiting the tab
// with a stale report kicks a build — a real LLM run — exactly like the overview.
export function usePatientRisksGaps(patientId: string | undefined) {
  const client = useClient()
  const ops = useMemo<AiReportOps<RisksGapsData>>(
    () => ({
      read: (id, checkUpToDate) => client.patientRisksGaps({ id, checkUpToDate }),
      refresh: (id, force) => client.patientRisksGapsRefresh(force ? { id, force: true } : { id }),
      label: 'risks & gaps report',
    }),
    [client],
  )
  return usePatientAiReport(patientId, ops)
}
